import * as React from "react";
import { Alert, Button, Card } from "antd";
import { CardTitle } from "../../../../components/antd/card/CardTitle";
import { CardContent } from "../../../../components/antd/card/CardContent";
import { CpInput } from "../../../gvc/testNewForm/CpInput";
import { CpRadioCardGroup } from "../../../gvc/testNewForm/CpRadioCardGroup";
import { CpLabel } from "../../../gvc/testNewForm/CpLabel";
import { CpFormItem } from "../../../gvc/testNewForm/CpFormItem";
import { CpInputDescription } from "../../../gvc/testNewForm/CpInputDescription";
import { CpErrorWarning } from "../../../gvc/testNewForm/CpErrorWarning";
import { CpErrorWarningArrayIcon } from "../../../gvc/testNewForm/CpErrorWarningArrayIcon";
import { CpKindSelect } from "../../../gvc/testNewForm/temp/CpKindSelect";
import { CpLabelTooltip } from "../../../gvc/testNewForm/CpLabelTooltip";
import { CpTooltip } from "../../../gvc/testNewForm/CpTooltip";
import { formSetValue, useCpFormValue } from "../../../gvc/testNewForm/utils";
import { useCpFormContext } from "../../../../components/form/useCpFormContext";
import { v4 as uuidv4 } from "uuid";
import { getDefaultContainerInput } from "../utils/utils";

interface Props {}
export const General: React.FC<Props> = ({}) => {
  const { id: formId } = useCpFormContext();
  const isCreate = useCpFormValue(`meta.isCreate`);
  const showExampleAlert = useCpFormValue(`meta.showExampleAlert`);

  function handleUseExampleWorkload() {
    formSetValue(formId, `meta.showExampleAlert`, false);
    // Set workload name and type
    formSetValue(formId, `name`, "httpbin-example");
    formSetValue(formId, `type`, "standard");

    // Create example container with external image
    const exampleContainer = getDefaultContainerInput();
    exampleContainer.name.value = "httpbin";
    exampleContainer.image.isExternal = true;
    exampleContainer.image.externalValue = "kennethreitz/httpbin";
    exampleContainer.ports = [{ id: uuidv4(), protocol: "http", number: "80" }];

    formSetValue(formId, `containers`, [exampleContainer]);
    formSetValue(formId, `meta.activeKeyContainer`, `container-${exampleContainer.id}`);

    // Set capacity AI off
    formSetValue(formId, `defaultOptions.capacityAI`, false);

    // Set firewall to public (inbound and outbound)
    formSetValue(formId, `firewallConfig.external.inbound.public`, true);
    formSetValue(formId, `firewallConfig.external.outbound.public`, true);
  }

  return (
    <>
      {isCreate && showExampleAlert ? (
        <Alert
          type="info"
          className="mb-4"
          style={{ maxWidth: 800 }}
          message="Do you want to use an example workload spec to get started?"
          action={<Button onClick={handleUseExampleWorkload}>Use Example Workload</Button>}
        />
      ) : null}
      <div className="cpln-form-section cpln-ant-card-container">
        <Card>
          <CardTitle>
            Workload
            <CpErrorWarningArrayIcon names={[`name`, `description`, `type`, `gvc`]} />
          </CardTitle>
          <CardContent>
            <CpFormItem>
              <CpLabelTooltip>
                <CpLabel name="name">Name</CpLabel>
                <CpTooltip tooltipProps={{ title: `Cannot be changed after creation (ok, my-item, item1)` }} />
              </CpLabelTooltip>
              <CpInput name={`name`} style={{ width: 600 }} />
              <CpErrorWarning name={`name`} />
            </CpFormItem>
            <CpInputDescription namePath="name" descriptionPath="description" />
            <CpFormItem>
              <CpLabelTooltip>
                <CpLabel name="gvc">GVC</CpLabel>
                <CpTooltip tooltipProps={{ title: `Cannot be changed after creation` }} />
              </CpLabelTooltip>
              <CpKindSelect name={`gvc`} kind="gvc" optionValue="name" style={{ width: 600 }} />
              <CpErrorWarning name={`gvc`} />
            </CpFormItem>
            <CpFormItem>
              <CpLabel name="type">Type</CpLabel>
              <div style={{ maxWidth: "fit-content" }}>
                <CpRadioCardGroup
                  name={`type` as any}
                  grid
                  options={[
                    {
                      name: "Standard",
                      value: "standard",
                      terms: ["Does not scale to zero", "Multiple or no ports can be exposed"],
                    },
                    {
                      name: "Stateful",
                      value: "stateful",
                      terms: [
                        "Same features as Standard",
                        "Has stable pod identity",
                        "Supports volume sets for persistent data",
                      ],
                    },
                    {
                      name: "Cron",
                      value: "cron",
                      terms: [
                        "Runs on a schedule, not for always-on services",
                        "Cannot expose a port",
                        "Must exit upon completion",
                      ],
                    },
                    {
                      name: "Serverless",
                      value: "serverless",
                      terms: ["Can scale to zero when idle", "Exposes a single port only", "Faster scaling decisions"],
                    },
                  ].map((t) => ({
                    label: t.name,
                    value: t.value,
                    boldLabel: true,
                    children: (
                      <div style={{ minWidth: 250 }}>
                        <ul className="mt-2">
                          {t.terms.map((term) => (
                            <li key={term} className="text-sm ml-4" style={{ listStyleType: "disc" }}>
                              {term}
                            </li>
                          ))}
                        </ul>
                      </div>
                    ),
                  }))}
                />
              </div>
              <CpErrorWarning name={`type`} />
            </CpFormItem>
          </CardContent>
        </Card>
      </div>
    </>
  );
};
